import React, { useCallback, useEffect, useState } from "react";

import { v4 as uuid } from "uuid";

import { getVideoDetails } from "services";

import { useAppType, useCancelAsyncOnUnmount, useInView } from "utils";

import { GatedVimeo, LazyLoadImage } from "components";

const id = uuid();

type VideoPlaceholderImages = {
  desktop?: string;
  mobile?: string;
  link?: string;
};

type VideoPlayerProps = {
  autoplay?: boolean;
  disableDataLoad?: boolean;
  disableResponsive?: boolean;
  ignoreIntersectionObserver?: boolean;
  loop?: boolean;
  placeholderImages?: VideoPlaceholderImages | string | null;
  vimeoId?: string;
  dataTestId?: string;
};

// Resolves the placeholder image down to a single URL string (or null) so it can be passed straight to an <img> src.
const getPlaceholderImage = (appType: { desktop?: boolean }, placeholderImages: VideoPlaceholderImages | string | null): string | null => {
  if (!placeholderImages) {
    return null;
  }
  if (typeof placeholderImages === "string") {
    return placeholderImages;
  }
  if (appType.desktop && placeholderImages.desktop) {
    return placeholderImages.desktop;
  }
  if (placeholderImages.mobile) {
    return placeholderImages.mobile;
  }
  if (placeholderImages.link) {
    return placeholderImages.link;
  }
  return null;
};

export const VideoPlayer = ({
  autoplay = false,
  disableDataLoad,
  disableResponsive,
  ignoreIntersectionObserver,
  loop,
  placeholderImages: placeholderImagesFromProps,
  vimeoId,
  dataTestId,
}: VideoPlayerProps) => {
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [videoInProgress, setVideoInProgress] = useState(false);
  const [placeholderImages, setPlaceholderImages] = useState<VideoPlaceholderImages | string | null>(placeholderImagesFromProps || null);
  const signal = useCancelAsyncOnUnmount()();
  const appType = useAppType();

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    trackVisibility: true,
    delay: 100,
  });

  const hardcodedHeight = () => {
    if (appType.desktop) {
      return 340;
    }
    if (appType.mobile) {
      return 200;
    }
    if (appType.smallDesktop) {
      return 200;
    }
    return 340;
  };

  const loadVideoDetails = useCallback(() => {
    if (!vimeoId) return;

    getVideoDetails({
      vimeoId,
      signal,
    }).then((res) => {
      // The global axios response interceptor unwraps to `.data` at runtime but is typed as AxiosResponse, so cast to the real payload shape.
      const { placeholderImages: fetchedPlaceholderImages } = res as unknown as { placeholderImages: VideoPlaceholderImages };
      setPlaceholderImages(fetchedPlaceholderImages);
      setLoading(false);
    });
  }, [signal, vimeoId]);

  useEffect(() => {
    if (!disableDataLoad && vimeoId) {
      loadVideoDetails();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const placeHolderImage = getPlaceholderImage(appType, placeholderImages);
  const fallbackImage = "/images/misc/play-image.svg";

  return (
    <div className="vimeo-container" data-testid={dataTestId} id={id} ref={ref}>
      <>
        {/* Fallback image for all the videos except the Homepage top one */}
        {(loading || !isVideoReady) && !autoplay && (
          <div className="skeleton-background">
            <LazyLoadImage alt="" src={fallbackImage} width="100px" height={hardcodedHeight()} />
          </div>
        )}

        {/* Fallback image for homepage top video */}
        {(loading || !isVideoReady || !videoInProgress) && autoplay && (
          <div className="skeleton-background">
            <LazyLoadImage alt="" src={fallbackImage} width="100px" height="100px" />
          </div>
        )}

        {/* Showing a placeholder image until the video starts */}
        {!videoInProgress && placeHolderImage && !autoplay && (
          <div>
            <LazyLoadImage alt="" className="video-placeholder" src={placeHolderImage} width="100%" height="100%" />
          </div>
        )}

        {(inView || ignoreIntersectionObserver) && (
          <GatedVimeo
            consentFallback={null}
            autoplay={autoplay}
            className={`vimeo-player ${!isVideoReady ? "hidden" : ""}`}
            playsInline
            showByline={false}
            showPortrait={false}
            showTitle={false}
            video={vimeoId ?? ""}
            background={autoplay}
            loop={loop}
            responsive={!disableResponsive}
            onReady={() => setIsVideoReady(true)}
            onProgress={() => setVideoInProgress(true)}
          />
        )}
      </>
      <div className="funky-shape-cut-out" />
    </div>
  );
};

export default VideoPlayer;
